import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  CardActions,
  Chip,
  Alert,
  CircularProgress,
  Button,
  Badge,
  useTheme,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  AdminPanelSettings as AdminIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  DirectionsCar as CarIcon,
  Schedule as ScheduleIcon,
  CreditCard as CardIcon,
  Notes as NotesIcon,
  Engineering as EngineeringIcon,
  Refresh as RefreshIcon,
  Build as BuildIcon,
  CheckCircle as CheckCircleIcon,
  Search as SearchIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  LocalShipping as ShippingIcon,
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import api from '../../services/api';
import AdminJobRequestModal from '../../components/AdminJobRequestModal';
import CompletedJobModal from '../../components/CompletedJobModal';

const JobRequestCard = ({ jobRequest, onCardClick, onDiscard }) => {
  const theme = useTheme();
  const { booking, technician, items, technician_notes, services, is_first_request, gsf_order_status, gsf_order_confirmation, payment } = jobRequest;

  // Priority configuration
  const priorityConfig = {
    high: { color: '#f44336', symbol: '🔴', label: 'High Priority', borderColor: '#f44336' },
    medium: { color: '#ff9800', symbol: '🟡', label: 'Medium Priority', borderColor: '#ff9800' },
    low: { color: '#4caf50', symbol: '🟢', label: 'Low Priority', borderColor: '#4caf50' }
  };

  // Get the highest priority from all items
  const getHighestPriority = (items) => {
    const priorities = items.map(item => item.priority);
    if (priorities.includes('high')) return 'high';
    if (priorities.includes('medium')) return 'medium';
    return 'low';
  };

  const highestPriority = getHighestPriority(items);
  const currentPriority = priorityConfig[highestPriority];

  const handleCardClick = () => {
    onCardClick(jobRequest);
  };

  const handleDiscard = (e) => {
    e.stopPropagation();
    if (window.confirm(`Are you sure you want to discard this parts request for ${booking.customer_name}?`)) {
      onDiscard(jobRequest.id);
    }
  };

  return (
    <Card
      onClick={handleCardClick}
      sx={{
        mb: 2,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: theme.shadows[4],
        },
        borderLeft: `4px solid ${currentPriority.borderColor}`,
      }}
    >
      <CardContent sx={{ p: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
          <Typography variant="subtitle2" fontWeight="bold">
            {booking.customer_name}
          </Typography>
          <Box display="flex" gap={0.5} flexWrap="wrap" alignItems="center">
            <Chip
              label={`${currentPriority.symbol} ${currentPriority.label}`}
              size="small"
              sx={{
                fontSize: '0.7rem',
                backgroundColor: `${currentPriority.color}20`,
                color: currentPriority.color,
                border: `1px solid ${currentPriority.color}40`,
                fontWeight: 'bold'
              }}
            />
            <Chip
              label="Parts Request"
              size="small"
              color="warning"
              sx={{ fontSize: '0.7rem' }}
            />
            {payment?.status === 'completed' && (
              <Chip
                label="PAID"
                size="small"
                color="success"
                sx={{ fontSize: '0.7rem', fontWeight: 'bold' }}
              />
            )}
            {gsf_order_status === 'ordered' && (
              <Tooltip title={`GSF Order: ${gsf_order_confirmation || 'Confirmed'}`}>
                <Chip
                  icon={<ShippingIcon sx={{ fontSize: 14 }} />}
                  label="Parts Ordered"
                  size="small"
                  sx={{
                    fontSize: '0.7rem',
                    backgroundColor: '#e3f2fd',
                    color: '#1976d2',
                    '& .MuiChip-icon': { color: '#1976d2' }
                  }}
                />
              </Tooltip>
            )}
            {gsf_order_status === 'failed' && (
              <Tooltip title="GSF order failed - check Attention alerts">
                <Chip
                  label="Order Failed"
                  size="small"
                  color="error"
                  sx={{ fontSize: '0.7rem' }}
                />
              </Tooltip>
            )}
            {gsf_order_status === 'manual' && (
              <Tooltip title={`Manually ordered${gsf_order_confirmation && gsf_order_confirmation !== 'MANUAL' ? `: ${gsf_order_confirmation}` : ''}`}>
                <Chip
                  icon={<ShippingIcon sx={{ fontSize: 14 }} />}
                  label="Manual Order"
                  size="small"
                  sx={{
                    fontSize: '0.7rem',
                    backgroundColor: '#e8f5e9',
                    color: '#2e7d32',
                    '& .MuiChip-icon': { color: '#2e7d32' }
                  }}
                />
              </Tooltip>
            )}
            <Tooltip title="Discard this request">
              <IconButton
                size="small"
                onClick={handleDiscard}
                sx={{
                  ml: 0.5,
                  color: 'error.main',
                  '&:hover': {
                    backgroundColor: 'error.light',
                    color: 'error.contrastText',
                  }
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Box display="flex" alignItems="center" mb={0.5}>
          <CarIcon sx={{ fontSize: 14, mr: 0.5, color: 'text.secondary' }} />
          <Typography variant="caption" color="text.secondary">
            {booking.vehicle_info}
          </Typography>
        </Box>

        <Box display="flex" alignItems="center" mb={0.5}>
          <ScheduleIcon sx={{ fontSize: 14, mr: 0.5, color: 'text.secondary' }} />
          <Typography variant="caption" color="text.secondary">
            {new Date(booking.booking_date).toLocaleDateString()}
            {booking.time_of_day && ` (${booking.time_of_day})`}
          </Typography>
        </Box>

        <Box display="flex" alignItems="center" mb={0.5}>
          <EngineeringIcon sx={{ fontSize: 14, mr: 0.5, color: 'text.secondary' }} />
          <Typography variant="caption" color="text.secondary">
            Requested by: {technician.full_name} ({technician.employee_id})
          </Typography>
        </Box>

        {booking.customer_phone && (
          <Box display="flex" alignItems="center" mb={0.5}>
            <PhoneIcon sx={{ fontSize: 14, mr: 0.5, color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary">
              {booking.customer_phone}
            </Typography>
          </Box>
        )}

        {booking.total_cost > 0 && (
          <Box display="flex" alignItems="center" mb={0.5}>
            <CardIcon sx={{ fontSize: 14, mr: 0.5, color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary">
              Original: £{booking.total_cost.toFixed(2)}
            </Typography>
          </Box>
        )}

        {/* Items Preview */}
        <Box mt={1}>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 'bold' }}>
            Requested Items ({items.length}):
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
            {items.slice(0, 3).map((item, index) => {
              const itemPriorityConfig = priorityConfig[item.priority] || priorityConfig.medium;
              const paidIndicator = item.is_paid ? '✓ ' : '';
              return (
                <Chip
                  key={index}
                  label={`${paidIndicator}${itemPriorityConfig.symbol} ${item.quantity}x ${item.name}`}
                  size="small"
                  variant="outlined"
                  sx={{
                    fontSize: '0.6rem',
                    height: 20,
                    borderColor: item.is_paid ? '#4caf50' : `${itemPriorityConfig.color}40`,
                    color: item.is_paid ? '#4caf50' : itemPriorityConfig.color,
                    backgroundColor: item.is_paid ? '#4caf5010' : 'transparent',
                  }}
                />
              );
            })}
            {items.length > 3 && (
              <Chip
                label={`+${items.length - 3} more`}
                size="small"
                sx={{ fontSize: '0.6rem', height: 20 }}
              />
            )}
          </Box>
        </Box>

        {/* Services Preview (for first job requests) */}
        {is_first_request && services && services.length > 0 && (
          <Box mt={1}>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 'bold' }}>
              Services Included ({services.length}):
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
              {services.map((service, index) => (
                <Chip
                  key={index}
                  label={`🔧 ${service.service_type.name}`}
                  size="small"
                  variant="outlined"
                  color="primary"
                  sx={{
                    fontSize: '0.6rem',
                    height: 20
                  }}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Technician Notes Preview */}
        {technician_notes && (
          <Box mt={1}>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 'bold' }}>
              Technician Notes:
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              {technician_notes.length > 80
                ? `${technician_notes.substring(0, 80)}...`
                : technician_notes
              }
            </Typography>
          </Box>
        )}

        {/* Request Date */}
        <Box mt={1} pt={1} sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary">
            Requested: {new Date(jobRequest.created_at).toLocaleString()}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

const AdminBoard = () => {
  const [jobRequests, setJobRequests] = useState([]);
  const [awaitingPayment, setAwaitingPayment] = useState([]);
  const [completedJobs, setCompletedJobs] = useState([]);
  const [completedPagination, setCompletedPagination] = useState({ page: 1, has_more: false, total: 0 });
  const [loadingMoreCompleted, setLoadingMoreCompleted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedJobRequest, setSelectedJobRequest] = useState(null);
  const [adminModalOpen, setAdminModalOpen] = useState(false);
  const [selectedCompletedJobId, setSelectedCompletedJobId] = useState(null);
  const [completedJobModalOpen, setCompletedJobModalOpen] = useState(false);
  const [jobRequestSearch, setJobRequestSearch] = useState('');
  const [awaitingPaymentSearch, setAwaitingPaymentSearch] = useState('');
  const [completedJobSearch, setCompletedJobSearch] = useState('');

  const user = useSelector((state) => state.auth.user);

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchAdminBoardData();
    }
  }, [user]);

  const fetchAdminBoardData = async () => {
    try {
      setLoading(true);
      const response = await api.get('/v1/admin-board/');

      // Sort job requests by highest item priority: high -> medium -> low
      const getHighestPriority = (items) => {
        const priorities = items.map(item => item.priority);
        if (priorities.includes('high')) return 'high';
        if (priorities.includes('medium')) return 'medium';
        return 'low';
      };

      const sortedJobRequests = response.data.job_requests.sort((a, b) => {
        const priorityOrder = { high: 0, medium: 1, low: 2 };
        const aHighestPriority = getHighestPriority(a.items);
        const bHighestPriority = getHighestPriority(b.items);
        const aPriorityOrder = priorityOrder[aHighestPriority] !== undefined ? priorityOrder[aHighestPriority] : 1;
        const bPriorityOrder = priorityOrder[bHighestPriority] !== undefined ? priorityOrder[bHighestPriority] : 1;

        if (aPriorityOrder !== bPriorityOrder) {
          return aPriorityOrder - bPriorityOrder; // Sort by highest priority first
        }

        // If priorities are the same, sort by creation date (newest first)
        return new Date(b.created_at) - new Date(a.created_at);
      });

      setJobRequests(sortedJobRequests);

      const awaitingPaymentData = response.data.awaiting_payment || [];
      setAwaitingPayment(awaitingPaymentData);

      const completedJobsData = response.data.completed_jobs || [];
      setCompletedJobs(completedJobsData);
      setCompletedPagination(response.data.completed_pagination || { page: 1, has_more: false, total: 0 });
    } catch (err) {
      setError('Failed to load admin board: ' + (err.response?.data?.error || err.message));
      console.error('DEBUG: Error fetching admin board:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadMoreCompletedJobs = async () => {
    if (loadingMoreCompleted || !completedPagination.has_more) return;

    try {
      setLoadingMoreCompleted(true);
      const nextPage = completedPagination.page + 1;
      const response = await api.get(`/v1/admin-board/?completed_page=${nextPage}`);

      const newJobs = response.data.completed_jobs || [];
      setCompletedJobs((prev) => [...prev, ...newJobs]);
      setCompletedPagination(response.data.completed_pagination || { page: nextPage, has_more: false, total: 0 });
    } catch (err) {
      console.error('Error loading more completed jobs:', err);
    } finally {
      setLoadingMoreCompleted(false);
    }
  };

  const handleCompletedJobsScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    // Load more when scrolled to 80% of the container
    if (scrollTop + clientHeight >= scrollHeight * 0.8) {
      loadMoreCompletedJobs();
    }
  };

  const handleJobRequestClick = (jobRequest) => {
    setSelectedJobRequest(jobRequest);
    setAdminModalOpen(true);
  };

  const handleCloseAdminModal = () => {
    setAdminModalOpen(false);
    setSelectedJobRequest(null);
  };

  const handleJobRequestCompleted = () => {
    setSuccess('Job request completed successfully!');
    fetchAdminBoardData(); // Refresh the data
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleCompletedJobClick = (jobId) => {
    setSelectedCompletedJobId(jobId);
    setCompletedJobModalOpen(true);
  };

  const handleCloseCompletedJobModal = () => {
    setCompletedJobModalOpen(false);
    setSelectedCompletedJobId(null);
  };

  const handleRefresh = () => {
    setError('');
    fetchAdminBoardData();
  };

  const handleDiscardJobRequest = async (jobRequestId) => {
    try {
      await api.post(`/v1/job-requests/${jobRequestId}/discard/`);
      setSuccess('Job request discarded successfully');
      fetchAdminBoardData(); // Refresh the data
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to discard job request: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleEditQuote = async (paymentId, customerName) => {
    if (!window.confirm(`Cancel this quote for ${customerName} and edit the pricing? The customer will receive a new payment link after you update the prices.`)) {
      return;
    }

    try {
      // Cancel the payment and reset job request status
      const response = await api.post(`/v1/payments/${paymentId}/cancel-for-edit/`);

      setSuccess('Quote cancelled. You can now edit and resend the payment link.');

      // Refresh board data - job will move from Awaiting Payment to Pending column
      await fetchAdminBoardData();

      // Find the job request in the updated data and open the modal
      // The job request ID is returned from the cancel endpoint
      const jobRequestId = response.data.job_request_id;
      if (jobRequestId) {
        // Find the job request in the refreshed data
        const updatedResponse = await api.get('/v1/admin-board/');
        const foundJobRequest = updatedResponse.data.job_requests.find(jr => jr.id === jobRequestId);
        if (foundJobRequest) {
          setSelectedJobRequest(foundJobRequest);
          setAdminModalOpen(true);
        }
      }

      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to cancel quote: ' + (err.response?.data?.error || err.message));
    }
  };

  // Filter job requests by search query
  const filteredJobRequests = jobRequests.filter((jobRequest) => {
    if (!jobRequestSearch) return true;

    const searchLower = jobRequestSearch.toLowerCase();
    const customerName = jobRequest.booking?.customer_name?.toLowerCase() || '';
    const registration = jobRequest.booking?.registration?.toLowerCase() || '';
    const vin = jobRequest.booking?.vin_number?.toLowerCase() || '';
    const phone = jobRequest.booking?.customer_phone?.toLowerCase() || '';
    const bookingId = jobRequest.booking?.id?.toString() || '';
    const technicianName = jobRequest.technician?.full_name?.toLowerCase() || '';

    return (
      customerName.includes(searchLower) ||
      registration.includes(searchLower) ||
      vin.includes(searchLower) ||
      phone.includes(searchLower) ||
      bookingId.includes(searchLower) ||
      technicianName.includes(searchLower)
    );
  });

  // Filter awaiting payment jobs by search query
  const filteredAwaitingPayment = awaitingPayment.filter((job) => {
    if (!awaitingPaymentSearch) return true;

    const searchLower = awaitingPaymentSearch.toLowerCase();
    const customerName = job.customer_name?.toLowerCase() || '';
    const registration = job.registration?.toLowerCase() || '';
    const phone = job.customer_phone?.toLowerCase() || '';
    const technicianName = job.technician?.toLowerCase() || '';

    return (
      customerName.includes(searchLower) ||
      registration.includes(searchLower) ||
      phone.includes(searchLower) ||
      technicianName.includes(searchLower)
    );
  });

  // Filter completed jobs by search query
  const filteredCompletedJobs = completedJobs.filter((job) => {
    if (!completedJobSearch) return true;

    const searchLower = completedJobSearch.toLowerCase();
    // Completed jobs have a flat structure, not nested
    const customerName = job.customer_name?.toLowerCase() || '';
    const registration = job.registration?.toLowerCase() || '';
    const vin = job.vin_number?.toLowerCase() || '';
    const phone = job.customer_phone?.toLowerCase() || '';
    const bookingId = job.booking_id?.toString() || job.id?.toString() || '';
    const technicianName = job.technician?.toLowerCase() || '';

    return (
      customerName.includes(searchLower) ||
      registration.includes(searchLower) ||
      vin.includes(searchLower) ||
      phone.includes(searchLower) ||
      bookingId.includes(searchLower) ||
      technicianName.includes(searchLower)
    );
  });

  // Check if user is admin
  if (user?.role !== 'admin') {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Access denied. This page is only available to admin users.
        </Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center">
          <AdminIcon sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" fontWeight="bold">
              Admin Board
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Review and price technician parts requests
            </Typography>
          </Box>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Columns Container */}
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          overflowX: 'auto',
          overflowY: 'hidden',
          pb: 2,
          '&::-webkit-scrollbar': {
            height: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#c1c1c1',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#a1a1a1',
          },
        }}
      >
        {/* Column 1: Pending Parts Requests */}
        <Paper
          sx={{
            minWidth: '400px',
            maxWidth: '400px',
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            height: 'calc(100vh - 200px)',
          }}
        >
          <Box display="flex" alignItems="center" mb={2}>
            <Badge badgeContent={filteredJobRequests.length} color="warning" sx={{ mr: 2 }}>
              <BuildIcon sx={{ fontSize: 24 }} />
            </Badge>
            <Box>
              <Typography variant="h6">
                Pending Parts Requests
              </Typography>
              <Typography variant="caption" color="text.secondary">
                🔴 High → 🟡 Medium → 🟢 Low
              </Typography>
            </Box>
          </Box>

          <TextField
            size="small"
            placeholder="Search by name, phone, VIN, reg, or booking #"
            value={jobRequestSearch}
            onChange={(e) => setJobRequestSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />

          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              pr: 1,
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: '#f1f1f1',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#c1c1c1',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: '#a1a1a1',
              },
            }}
          >
            {filteredJobRequests.length === 0 ? (
              <Box
                sx={{
                  textAlign: 'center',
                  py: 6,
                  color: 'text.secondary',
                }}
              >
                <BuildIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
                <Typography variant="h6" gutterBottom>
                  {jobRequests.length === 0 ? 'No pending requests' : 'No matches found'}
                </Typography>
                <Typography variant="body2">
                  {jobRequests.length === 0
                    ? 'When technicians request parts, they will appear here for pricing and approval.'
                    : 'Try adjusting your search criteria.'}
                </Typography>
              </Box>
            ) : (
              filteredJobRequests.map((jobRequest) => (
                <JobRequestCard
                  key={jobRequest.id}
                  jobRequest={jobRequest}
                  onCardClick={handleJobRequestClick}
                  onDiscard={handleDiscardJobRequest}
                />
              ))
            )}
          </Box>
        </Paper>

        {/* Column 2: Awaiting Payment */}
        <Paper
          sx={{
            minWidth: '400px',
            maxWidth: '400px',
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            height: 'calc(100vh - 200px)',
          }}
        >
          <Box display="flex" alignItems="center" mb={2}>
            <Badge badgeContent={filteredAwaitingPayment.length} color="warning" sx={{ mr: 2 }}>
              <CardIcon sx={{ fontSize: 24 }} />
            </Badge>
            <Box>
              <Typography variant="h6">
                Awaiting Payment
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Payment link sent, waiting for customer
              </Typography>
            </Box>
          </Box>

          <TextField
            size="small"
            placeholder="Search by name, phone, or reg"
            value={awaitingPaymentSearch}
            onChange={(e) => setAwaitingPaymentSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />

          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              pr: 1,
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: '#f1f1f1',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#c1c1c1',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: '#a1a1a1',
              },
            }}
          >
            {filteredAwaitingPayment.length === 0 ? (
              <Box
                sx={{
                  textAlign: 'center',
                  py: 6,
                  color: 'text.secondary',
                }}
              >
                <CardIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
                <Typography variant="h6" gutterBottom>
                  {awaitingPayment.length === 0 ? 'No jobs awaiting payment' : 'No matches found'}
                </Typography>
                <Typography variant="body2">
                  {awaitingPayment.length === 0
                    ? 'Jobs will appear here after payment links are sent.'
                    : 'Try adjusting your search criteria.'}
                </Typography>
              </Box>
            ) : (
              filteredAwaitingPayment.map((job) => (
                <Card
                  key={job.id}
                  sx={{
                    mb: 2,
                    borderLeft: '4px solid #ff9800',
                  }}
                >
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant="subtitle2" fontWeight="bold">
                      {job.customer_name}
                    </Typography>
                    <Chip
                      label="Awaiting Payment"
                      size="small"
                      sx={{
                        mt: 0.5,
                        mb: 1,
                        fontSize: '0.7rem',
                        backgroundColor: '#ff980020',
                        color: '#ff9800',
                        border: '1px solid #ff980040',
                      }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {job.vehicle_info} ({job.registration})
                    </Typography>
                    {job.customer_phone && (
                      <Typography variant="body2" color="text.secondary">
                        {job.customer_phone}
                      </Typography>
                    )}
                    <Typography variant="body2" color="text.secondary">
                      Technician: {job.technician}
                    </Typography>
                    {job.payment && (
                      <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold', color: '#ff9800' }}>
                        Amount: £{job.payment.amount.toFixed(2)}
                      </Typography>
                    )}
                  </CardContent>
                  {job.payment && (
                    <CardActions sx={{ px: 2, pb: 2, pt: 0 }}>
                      <Tooltip title="Cancel this quote and edit the pricing">
                        <Button
                          size="small"
                          variant="outlined"
                          color="warning"
                          startIcon={<EditIcon />}
                          onClick={() => handleEditQuote(job.payment.id, job.customer_name)}
                          sx={{ fontSize: '0.75rem' }}
                        >
                          Edit Quote
                        </Button>
                      </Tooltip>
                    </CardActions>
                  )}
                </Card>
              ))
            )}
          </Box>
        </Paper>

        {/* Column 3: Completed Jobs */}
        <Paper
          sx={{
            minWidth: '400px',
            maxWidth: '400px',
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            height: 'calc(100vh - 200px)',
          }}
        >
          <Box display="flex" alignItems="center" mb={2}>
            <Badge badgeContent={filteredCompletedJobs.length} color="success" sx={{ mr: 2 }}>
              <CheckCircleIcon sx={{ fontSize: 24 }} />
            </Badge>
            <Box>
              <Typography variant="h6">
                Completed Jobs
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Ready for final review
              </Typography>
            </Box>
          </Box>

          <TextField
            size="small"
            placeholder="Search by name, phone, VIN, reg, or booking #"
            value={completedJobSearch}
            onChange={(e) => setCompletedJobSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />

          <Box
            onScroll={handleCompletedJobsScroll}
            sx={{
              flex: 1,
              overflowY: 'auto',
              pr: 1,
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: '#f1f1f1',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#c1c1c1',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: '#a1a1a1',
              },
            }}
          >
            {filteredCompletedJobs.length === 0 ? (
              <Box
                sx={{
                  textAlign: 'center',
                  py: 6,
                  color: 'text.secondary',
                }}
              >
                <CheckCircleIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
                <Typography variant="h6" gutterBottom>
                  {completedJobs.length === 0 ? 'No completed jobs' : 'No matches found'}
                </Typography>
                <Typography variant="body2">
                  {completedJobs.length === 0
                    ? 'When technicians complete jobs, they will appear here for final review.'
                    : 'Try adjusting your search criteria.'}
                </Typography>
              </Box>
            ) : (
              filteredCompletedJobs.map((job) => (
                <Card
                  key={job.id}
                  sx={{
                    mb: 2,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: 4,
                    }
                  }}
                  onClick={() => handleCompletedJobClick(job.id)}
                >
                  <CardContent sx={{ p: 2 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {job.customer_name || 'N/A'}
                      </Typography>
                      <Chip
                        label="Completed"
                        size="small"
                        color="success"
                        sx={{ fontSize: '0.7rem' }}
                      />
                    </Box>

                    <Box display="flex" alignItems="center" mb={0.5}>
                      <CarIcon sx={{ fontSize: 14, mr: 0.5, color: 'text.secondary' }} />
                      <Typography variant="caption" color="text.secondary">
                        {job.vehicle_info} ({job.registration})
                      </Typography>
                    </Box>

                    <Box display="flex" alignItems="center" mb={0.5}>
                      <ScheduleIcon sx={{ fontSize: 14, mr: 0.5, color: 'text.secondary' }} />
                      <Typography variant="caption" color="text.secondary">
                        Booking: {new Date(job.booking_date).toLocaleDateString()}
                        {job.time_of_day && ` (${job.time_of_day})`}
                      </Typography>
                    </Box>

                    <Box display="flex" alignItems="center" mb={0.5}>
                      <EngineeringIcon sx={{ fontSize: 14, mr: 0.5, color: 'text.secondary' }} />
                      <Typography variant="caption" color="text.secondary">
                        Technician: {job.technician || 'N/A'}
                      </Typography>
                    </Box>

                    {job.customer_phone && (
                      <Box display="flex" alignItems="center" mb={0.5}>
                        <PhoneIcon sx={{ fontSize: 14, mr: 0.5, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">
                          {job.customer_phone}
                        </Typography>
                      </Box>
                    )}

                    {job.completed_at && (
                      <Box mt={1} pt={1} sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="caption" color="text.secondary">
                          Completed: {new Date(job.completed_at).toLocaleString()}
                        </Typography>
                      </Box>
                    )}

                    {/* Completion Reasons */}
                    {job.completion_reasons && job.completion_reasons.length > 0 && (
                      <Box mt={1} display="flex" flexWrap="wrap" gap={0.5}>
                        {job.completion_reasons.map((reason) => {
                          const reasonConfig = {
                            no_show: { label: 'No Show', color: 'error' },
                            vhc_ok: { label: 'VHC OK', color: 'success' },
                            customer_declined: { label: 'Declined', color: 'warning' },
                            warranty_goodwill: { label: 'Warranty', color: 'info' },
                            other: { label: 'Other', color: 'default' },
                          };
                          const config = reasonConfig[reason] || { label: reason, color: 'default' };
                          return (
                            <Chip
                              key={reason}
                              label={config.label}
                              size="small"
                              color={config.color}
                              sx={{ fontSize: '0.65rem', height: 20 }}
                            />
                          );
                        })}
                      </Box>
                    )}

                    {/* Completion Notes Preview */}
                    {job.completion_notes && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: 'block',
                          mt: 0.5,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          fontStyle: 'italic',
                        }}
                      >
                        Note: {job.completion_notes}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              ))
            )}

            {/* Loading indicator for infinite scroll */}
            {loadingMoreCompleted && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            )}

            {/* Show count when there are jobs */}
            {filteredCompletedJobs.length > 0 && !completedJobSearch && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', textAlign: 'center', py: 1 }}
              >
                Showing {completedJobs.length} of {completedPagination.total} completed jobs
                {completedPagination.has_more && ' (scroll for more)'}
              </Typography>
            )}
          </Box>
        </Paper>
      </Box>

      {/* Admin Job Request Modal */}
      <AdminJobRequestModal
        open={adminModalOpen}
        onClose={handleCloseAdminModal}
        jobRequest={selectedJobRequest}
        onComplete={handleJobRequestCompleted}
        onDiscard={handleJobRequestCompleted}
      />

      {/* Completed Job Modal */}
      <CompletedJobModal
        open={completedJobModalOpen}
        onClose={handleCloseCompletedJobModal}
        bookingId={selectedCompletedJobId}
      />
    </Box>
  );
};

export default AdminBoard;